/**
 * Financial Health Check (BE-15b)
 *
 * Checks the books (GL) against their own records - bank, wallets, receivables, payables, commissions, Thaklis
 * payable, VAT, cheques, fixed assets and the balance sheet equation - worked out by the backend on each call.
 * Uses the same SummaryCardGrid + DataTable components as Commissions, Expenses, etc.
 *
 * Route: /accounting/health
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Alert, Chip, LinearProgress,
} from '@mui/material';
import { Refresh, ExpandMore, ExpandLess } from '@mui/icons-material';
import { fontSize as fs, fontWeight as fw } from '../../utils/accounting/styles';
import { SummaryCardGrid, DataTable, CurrencyText } from '../../components/accounting';
import { formatCurrency } from '../../utils/accounting/formatters';
import { financeReportsService } from '../../services/accounting/financeReports.service';
import { serverMessage } from '../../utils/apiConvert';

// The backend sends an action code, not a route: each opens the page where it is done
const ACTIONS = {
  REVIEW_ACCOUNT: { label: 'Review', route: (id) => (id ? `/reports/wallet-bank?account=${id}` : '/reports/wallet-bank') },
  APPROVE_TOPUP: { label: 'Approve', route: () => '/wallets?tab=topups' },
  PAY_EXPENSE: { label: 'Pay', route: (id) => (id ? `/accounting/expenses/${id}` : '/accounting/expenses') },
  PAY_COMMISSIONS: { label: 'Pay', route: () => '/accounting/commissions' },
  FILE_VAT: { label: 'File', route: () => '/reports/vat-return' },
  PAY_VAT: { label: 'Pay', route: () => '/reports/vat-return' },
  REVIEW_CHEQUE: { label: 'Review', route: () => '/banking?tab=cheques' },
  RUN_DEPRECIATION: { label: 'Run', route: () => '/accounting/assets' },
  RECONCILE: { label: 'Reconcile', route: () => '/accounting/reconciliation' },
  CONTINUE_RECONCILIATION: { label: 'Continue', route: (id) => (id ? `/accounting/reconciliation/${id}` : '/accounting/reconciliation') },
  SET_LOCK_DATE: { label: 'Set lock date', route: () => '/settings?section=finance&tab=lock-date' },
  // a manual journal pre-filled with the 3009 side; the accountant picks Owner Capital or Retained Earnings
  CLEAR_OPENING_BALANCE_EQUITY: { label: 'Prepare journal', route: (id, item) => `/accounting/journal-entries/new?obe=${item?.amount ?? ''}` },
};

// Checks that count accounts, not money (BE-32): no amounts to show
const NO_AMOUNTS = new Set(['RECONCILIATION']);
const Dash = () => <Typography component="span" sx={{ fontSize: fs.xs, color: 'text.disabled' }}>—</Typography>;

const STATUS_CHIP = {
  pass: { label: 'Matched', bg: 'rgba(34,197,94,0.12)', color: '#16a34a' },
  warning: { label: 'Review', bg: 'rgba(245,158,11,0.12)', color: '#d97706' },
  error: { label: 'Error', bg: 'rgba(239,68,68,0.12)', color: '#dc2626' },
};

const FinancialHealthCheck = ({ currentUser }) => {
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(true);
  const [error, setError] = useState(null);
  const [notStarted, setNotStarted] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const handleRun = useCallback(async () => {
    setRunning(true);
    setError(null);
    try {
      setResult(await financeReportsService.getHealthCheck());
      setNotStarted(null);
    } catch (err) {
      const message = serverMessage(err, 'Failed to run the health check');
      // Before the ledger starts there is nothing to check: an empty state, not an error
      if (err?.response?.status === 400 && /ledger has not started/i.test(message)) {
        setNotStarted(message);
      } else {
        setError(message);
      }
    } finally {
      setRunning(false);
    }
  }, []);

  useEffect(() => { handleRun(); }, [handleRun]);

  // The backend sends PASS / WARNING / ERROR, errors first; the chips are keyed lower case
  const checks = (result?.checks || []).map((c) => ({ ...c, status: String(c.status).toLowerCase(), items: c.items || [] }));
  const warnings = result?.warnings || 0;
  const errors = result?.errors || 0;

  // Sort: warnings/errors first
  const sorted = [...checks].sort((a, b) => {
    const o = { error: 0, warning: 1, pass: 2 };
    return (o[a.status] ?? 2) - (o[b.status] ?? 2);
  });

  const summaryCards = [
    { label: 'Total Checks', value: result?.total ?? 0, suffix: 'checks', color: '#3b82f6' },
    { label: 'Passed', value: result?.passed ?? 0, suffix: 'checks', color: '#22c55e' },
    { label: 'Warnings', value: warnings, suffix: 'checks', color: '#f59e0b' },
    { label: 'Errors', value: errors, suffix: 'checks', color: '#ef4444' },
  ];

  const columns = [
    {
      key: 'name', label: 'Check', width: '30%',
      render: (r) => (
        <Box>
          <Typography sx={{ fontSize: fs.xs, fontWeight: fw.medium }}>{r.name}</Typography>
          {r.note && <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', lineHeight: 1.3 }}>{r.note}</Typography>}
        </Box>
      ),
    },
    {
      key: 'books', label: 'Books (GL)', width: '15%', align: 'right',
      render: (r) => (NO_AMOUNTS.has(r.id) ? <Dash /> : <CurrencyText amount={r.books} />),
    },
    {
      key: 'actual', label: 'Actual', width: '15%', align: 'right',
      render: (r) => (NO_AMOUNTS.has(r.id) ? <Dash /> : <CurrencyText amount={r.actual} />),
    },
    {
      key: 'difference', label: 'Difference', width: '15%', align: 'right',
      render: (r) => Number(r.difference) !== 0
        ? <Typography sx={{ fontSize: fs.xs, color: '#d97706', fontWeight: fw.semibold }}>{formatCurrency(r.difference)}</Typography>
        : <Typography sx={{ fontSize: fs.xs, color: '#22c55e' }}>—</Typography>,
    },
    {
      key: 'status', label: 'Status', width: '12%',
      render: (r) => {
        const s = STATUS_CHIP[r.status];
        return <Chip label={s.label} size="small" sx={{ bgcolor: s.bg, color: s.color, fontSize: '0.65rem', height: 22 }} />;
      },
    },
    {
      key: 'actions', label: '', width: '5%',
      render: (r) => r.items.length > 0 ? (
        expandedId === r.id ? <ExpandLess sx={{ fontSize: 18, color: 'text.secondary' }} /> : <ExpandMore sx={{ fontSize: 18, color: 'text.secondary' }} />
      ) : null,
    },
  ];

  return (
    <Box sx={{ p: 2, width: 0, minWidth: '100%', boxSizing: 'border-box' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography sx={{ fontSize: fs.md, fontWeight: fw.semibold }}>Financial Health Check</Typography>
        <Button
          size="small" variant="contained" startIcon={<Refresh />}
          onClick={handleRun} disabled={running}
          sx={{ textTransform: 'none' }}
        >
          {running ? 'Checking...' : 'Run Checks'}
        </Button>
      </Box>

      {running && <LinearProgress sx={{ mb: 1 }} />}

      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}
      {notStarted && (
        <Alert severity="info" sx={{ mb: 1.5, fontSize: fs.xs }}>
          Nothing to check yet: {notStarted}
        </Alert>
      )}
      {result?.checkedAt && !running && (
        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 1 }}>
          Checked {new Date(result.checkedAt).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}
        </Typography>
      )}

      {/* Summary Cards — same component as Commissions/Expenses */}
      <SummaryCardGrid cards={summaryCards} loading={running} />

      {/* Checks Table — same DataTable as everywhere else */}
      <Box sx={{ mt: 1.5 }}>
        <DataTable
          columns={columns}
          data={sorted}
          loading={running}
          emptyMessage={notStarted ? 'The ledger has not started yet.' : 'No checks to display.'}
          pagination={false}
          onRowClick={(row) => {
            if (row.items.length > 0) {
              setExpandedId(expandedId === row.id ? null : row.id);
            }
          }}
        />
        {/* Expanded details rendered after DataTable rows */}
        {sorted.map(row => expandedId === row.id && row.items.length > 0 && (
          <Box key={row.id} sx={{ bgcolor: 'action.hover', px: 3, py: 1, border: '1px solid', borderColor: 'divider', borderTop: 'none', mx: 0 }}>
            {row.items.map((item, i) => {
              const action = ACTIONS[item.action];
              return (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', py: 0.5, gap: 2 }}>
                  <Typography sx={{ fontSize: fs.xs, color: 'text.secondary', flex: 1 }}>{item.text}</Typography>
                  {item.amount !== null && item.amount !== undefined && (
                    <Typography sx={{ fontSize: fs.xs, color: 'text.secondary' }}>{formatCurrency(item.amount)}</Typography>
                  )}
                  {action ? (
                    <Button
                      size="small" variant="contained"
                      onClick={() => navigate(action.route(item.entityId, item))}
                      sx={{ textTransform: 'none', fontSize: '0.65rem', py: 0.25, px: 1.5, minWidth: 0 }}
                    >
                      {action.label}
                    </Button>
                  ) : <Box sx={{ minWidth: 48 }} />}
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>

      {/* AI hint */}
      {(warnings + errors > 0) && (
        <Alert severity="info" sx={{ mt: 1.5, fontSize: fs.xs }}>
          {warnings + errors} items need attention — review and fix to keep your books accurate.
        </Alert>
      )}
    </Box>
  );
};

export default FinancialHealthCheck;
